import React, { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

const PIE_FILL_DURATION_MS = 850;
const PIE_FILL_DELAY_MS = 120;
const VIEWBOX_SIZE = 240;
const BASE_RADIUS = 96;
const BASE_STROKE = 32;
const GAP_SIZE = 2;

const GLOW_GRADIENTS = {
  default: [
    { offset: '0%', stopColor: 'var(--c-blue-1)', stopOpacity: 0.15 },
    { offset: '100%', stopColor: 'var(--c-black)', stopOpacity: 0.1 },
  ],
  neutral: [
    { offset: '0%', stopColor: 'var(--text3)', stopOpacity: 0.18 },
    { offset: '100%', stopColor: 'var(--c-black)', stopOpacity: 0.08 },
  ],
};

function buildSegments(data, circumference, gapSize = GAP_SIZE) {
  const total = Math.max(data.reduce((sum, part) => sum + part.value, 0), 0);
  if (total === 0) return { segments: [], total: 0 };

  let currentOffset = 0;
  const segments = data.map((part, index) => {
    if (part.value === 0) {
      return { ...part, dash: 0, offset: 0, index };
    }

    const portion = part.value / total;
    const rawDash = portion * circumference;
    const dash = Math.max(rawDash - gapSize, 0);
    const segment = {
      ...part,
      dash,
      offset: currentOffset,
      index,
    };
    currentOffset -= rawDash;
    return segment;
  });

  return { segments, total };
}

export default function PieChart({
  data = [],
  label = 'Total',
  sublabel,
  centerValue,
  animate = false,
  animationEpoch = null,
  responsive = false,
  size = VIEWBOX_SIZE,
  className = '',
  glowVariant = 'default',
  emptyStroke = 'var(--border)',
}) {
  const [segmentsReady, setSegmentsReady] = useState(!animate);
  const animatedSignatureRef = useRef(null);
  const gradientId = useId().replace(/:/g, '');
  const glowStops = GLOW_GRADIENTS[glowVariant] || GLOW_GRADIENTS.default;

  const radius = BASE_RADIUS;
  const strokeWidth = BASE_STROKE;
  const center = VIEWBOX_SIZE / 2;
  const circumference = 2 * Math.PI * radius;

  const { segments, total } = useMemo(
    () => buildSegments(data, circumference),
    [data, circumference],
  );

  const dataSignature = useMemo(
    () => data.map((part) => `${part.label}:${part.value}:${part.color}`).join('|'),
    [data],
  );

  const isEmpty = total === 0;
  const displayTotal = centerValue !== undefined && centerValue !== null
    ? centerValue
    : total;
  const displaySublabel = sublabel !== undefined ? sublabel : label;

  const animationKey = animationEpoch != null
    ? String(animationEpoch)
    : dataSignature;

  useLayoutEffect(() => {
    if (!animate || isEmpty) {
      setSegmentsReady(true);
      return undefined;
    }
    if (animatedSignatureRef.current === animationKey) {
      setSegmentsReady(true);
      return undefined;
    }
    animatedSignatureRef.current = animationKey;
    setSegmentsReady(false);
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSegmentsReady(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [animate, isEmpty, animationKey]);

  const wrapperStyle = responsive
    ? { position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }
    : {
      position: 'relative',
      width: size,
      height: size,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

  const svgDimensions = responsive
    ? { width: '100%', height: '100%' }
    : { width: size, height: size };

  return (
    <div className={['custom-pie-chart-wrapper', className].filter(Boolean).join(' ')} style={wrapperStyle}>
      <svg
        {...svgDimensions}
        viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
        style={{ filter: 'drop-shadow(0px 4px 12px rgba(0, 0, 0, 0.05)) drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.03))' }}
        role="presentation"
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            {glowStops.map((stop) => (
              <stop
                key={stop.offset}
                offset={stop.offset}
                stopColor={stop.stopColor}
                stopOpacity={stop.stopOpacity}
              />
            ))}
          </linearGradient>
        </defs>

        {isEmpty ? (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={emptyStroke}
            strokeWidth={strokeWidth}
          />
        ) : (
          <>
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth={strokeWidth + 4}
              style={{ opacity: 0.6 }}
            />
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="var(--c-white)"
              strokeWidth={strokeWidth}
            />
            {segments.map((seg) => {
              if (seg.dash <= 0) return null;
              const dasharray = segmentsReady
                ? `${seg.dash} ${circumference - seg.dash}`
                : `0 ${circumference}`;
              return (
                <circle
                  key={seg.label}
                  className={animate ? 'pie-chart-segment' : undefined}
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke={seg.color}
                  strokeWidth={strokeWidth}
                  strokeLinecap="butt"
                  strokeDasharray={dasharray}
                  strokeDashoffset={seg.offset}
                  transform={`rotate(-90 ${center} ${center})`}
                  style={animate && segmentsReady ? {
                    transitionProperty: 'stroke-dasharray',
                    transitionDuration: `${PIE_FILL_DURATION_MS}ms`,
                    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
                    transitionDelay: `calc(${PIE_FILL_DELAY_MS}ms + var(--pie-chart-stagger, 0ms) + ${seg.index * 90}ms)`,
                  } : { transition: 'none' }}
                />
              );
            })}
          </>
        )}
      </svg>

      <div
        className="pie-chart-center"
        style={{
          position: 'absolute',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        <span
          className={animate ? 'pie-chart-center__value' : undefined}
          style={{ fontSize: '32px', fontWeight: '600', color: 'var(--c-black)', lineHeight: '1' }}
        >
          {displayTotal}
        </span>
        <span
          className={animate ? 'pie-chart-center__label' : undefined}
          style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text2)', marginTop: '2px' }}
        >
          {displaySublabel}
        </span>
      </div>
    </div>
  );
}
